import fetchIntercept from 'fetch-intercept';
import { memoize } from 'lodash-es';

import {
  BASE_PATH, INVALID_CSRF_TOKEN_TEXT, routes
} from './constants';

const csrfTokenMetaTagName = 'csrf-token';
const csrfMetaTagSelector = `meta[name=${csrfTokenMetaTagName}]`;

// Helper to fetch CSRF token that is stored in the header in a meta tag
const getCSRFTokenFromMeta = () => document.querySelector(csrfMetaTagSelector)?.getAttribute('content');

// Helper that gets a new csrf token and stores it in a meta tag in the header
const getAndStoreCSRFToken = async () => {
  const response = await getJson('/csrf-token');
  const { csrfToken } = response;
  if (csrfToken) {
    if (getCSRFTokenFromMeta() == null) {
      // Create meta tag if it doesn't exist
      const meta = document.createElement('meta');
      meta.name = csrfTokenMetaTagName;
      document.head.appendChild(meta);
    }
    // Write to meta tag
    const csrfMeta = document.querySelector(csrfMetaTagSelector);
    csrfMeta.content = csrfToken;
    return csrfToken;
  }
  // Gracefully fail if csrf token is not present, will raise an "Invalid CSRF token" error from the API
  return '';
};

// Helper that returns a found CSRF token or fetches a new one for requests
const getCSRFToken = memoize(async () => {
  let csrfToken = getCSRFTokenFromMeta();
  if (!csrfToken) {
    csrfToken = await getAndStoreCSRFToken();
  }
  return csrfToken;
});

const handleResponseError = async (err) => {
  if (err.status === 401) {
    window.location.href = BASE_PATH + routes.login;
  }

  if (err.status === 403) {
    const { error, url } = await err.json();

    if (error && (error.message === INVALID_CSRF_TOKEN_TEXT)) {
      const csrfError = { url, message: error.message };
      return Promise.reject(csrfError);
    }
    window.location.href = `${BASE_PATH}${routes.error}/${err.status}`;
  }

  return Promise.reject(err);
};

const isJsonResponse = (response) => {
  const contentType = response.headers.get('content-type');

  return contentType && contentType.includes('application/json');
};

fetchIntercept.register({
  request: (url, config) => {
    const defaultConfig = {
      credentials: 'same-origin'
    };
    return [url, { ...defaultConfig, ...config }];
  },
  response: (response) => {
    if (!response.ok) {
      return handleResponseError(response);
    }
    if (isJsonResponse(response)) {
      return response.json();
    }
    return response;
  }
});

export const get = (path) =>
  fetch(path);

export const postJson = async (path, data, csrfToken = true, expectNonJson = false) => {
  let headers = {
    Accept: expectNonJson ? '*/*' : 'application/json',
    'Content-Type': 'application/json'
  };
  // If we pass an empty CSRF-Token header it will cause an "invalid CSRF token" error
  if (csrfToken) {
    headers = {
      ...headers,
      'CSRF-Token': await getCSRFToken()
    };
  }
  return fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(data)
  });
};

export const putJson = async (path, data) => fetch(path, {
  method: 'PUT',
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'CSRF-Token': await getCSRFToken()
  },
  body: JSON.stringify(data)
});

export const patchJson = async (path, data) =>
  fetch(path, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'CSRF-Token': await getCSRFToken()
    },
    body: JSON.stringify(data)
  });

export const getJson = (path) => fetch(path, {
  headers: {
    Accept: 'application/json'
  }
});

export const del = async (path) =>
  fetch(path, {
    method: 'DELETE',
    headers: {
      'CSRF-Token': await getCSRFToken()
    }
  });
